import React, { Fragment } from 'react'
import {Box, Button, CircularProgress, Grid, Stack} from '@mui/material';
import importedStyles from '../Utilities/styles'
import DashboardCard from '../Utilities/DashboardCard';
import DataTeaser from '../Utilities/DataTeaser';
import usePerformanceMetrics from '../Utilities/hooks/usePerformanceMetrics';
import useQualityScoreMetrics from '../Utilities/hooks/useQualityScoreMetrics';
import {formatDuration} from "_helpers";
import _ from 'lodash';
import { Link as RouterLink } from 'react-router-dom';

const InfoModalContent = () => (
	<Box component={'dl'} sx={importedStyles.definitionList}>
		<Fragment>
			<Box component={'dt'} sx={importedStyles.definitionTitle}>
				Responses in less than 90 minutes
			</Box>
			<Box component={'dd'} sx={importedStyles.definitionText}>
				We calculate the median first response time across all messages. Our industry benchmarking data is from&nbsp;
				<a href='https://www.gorgias.com/blog/average-response-time#:~:text=customer%20support%20channel.-,Average%20response%20time%20benchmarks%20by%20channel,-Here%20are%20best' target='_blank'>Gorgias</a>.
			</Box>
		</Fragment>
		<Fragment>
			<Box component={'dt'} sx={importedStyles.definitionTitle}>
				Customer questions solved
			</Box>
			<Box component={'dd'} sx={importedStyles.definitionText}>
				Total number of customer questions where your Remarkable AI team replied to the customer. This excludes customer messages that did not need a response.
			</Box>
		</Fragment>
		<Fragment>
			<Box component={'dt'} sx={importedStyles.definitionTitle}>
				CSAT
			</Box>
			<Box component={'dd'} sx={importedStyles.definitionText}>
				This customer satisfaction score is pulled from your help desk.
			</Box>
		</Fragment>
	</Box>
)

export default function PerformanceSection({ startDate, endDate }) {
	const performanceMetrics = usePerformanceMetrics(startDate, endDate);
	const qualityScoreMetrics = useQualityScoreMetrics(startDate, endDate);
	const duration = performanceMetrics.data ? formatDuration(performanceMetrics.data.answerRateCompareToAvg) : '-';
	
	return (
		<DashboardCard
			isLoading={performanceMetrics.loading}
			headerTitle='Performance metrics'
			infoModalTitle='Conversion metrics calculations'
			infoModalChildren={<InfoModalContent />}>

			{
				!performanceMetrics.loading &&
				<Grid container columns={4} spacing={3} sx={styles.root}>
					<Grid item xs={4} md={1}>
						<DataTeaser
							stackStyle={styles.dataTeaser}
							label={'Responses in <90 Min'}
							entry={{ value: performanceMetrics.data ? performanceMetrics.data.responsesLessThan90Minutes : null, formatStyle: 'percent' }}
							descriptionChildren={<>
								<Box>Average first response time: { duration || '0 m' }.</Box>
								<Box>Industry average: 12 hours.</Box>
							</>}
						/>
						<Button sx={styles.viewDetailsBtn}
							component={RouterLink}
							to={{ pathname: `/support-details`,
									search: `?startDate=${startDate}&endDate=${endDate}&pageFilter=responseTime`
							}}
							variant="text">
								View details
						</Button>
					</Grid>
					<Grid item xs={4} md={1}>
						<DataTeaser
							stackStyle={styles.dataTeaser}
							label={'Conversations Resolved'}
							entry={{ value: performanceMetrics.data ? performanceMetrics.data.answerRate : null, formatStyle: 'percent' }}
							descriptionChildren={<>
								<Box>
									Total number of support tickets resolved by Remarkable AI
								</Box>
							</>}
						/>
						<Button sx={styles.viewDetailsBtn}
								component={RouterLink}
								to={{ pathname: `/support-details`,
									search: `?startDate=${startDate}&endDate=${endDate}&pageFilter=answerRate`
								}}
								variant="text">
							View details
						</Button>
					</Grid>
					{
					    qualityScoreMetrics.loading ? (
                            <Grid item xs={4} md={1}>
                                <Stack sx={styles.dataTeaser}>
                                    <Box component={'h4'} sx={styles.label}>Customer Satisfaction</Box>
                                    <Box sx={styles.bodyLoading}>
                                        <CircularProgress sx={styles.bodyLoadingSpinner} />
                                    </Box>
                                </Stack>
                        	</Grid>
                        ) : (
                            _.get(qualityScoreMetrics.data, 'csat') >= 0 && <Grid item xs={4} md={1}>
                                <DataTeaser
                                    stackStyle={styles.dataTeaser}
                                    label={'CSAT'}
                                    entry={{ value: qualityScoreMetrics.data ? qualityScoreMetrics.data.csat : null, formatStyle: 'percent' }}
                                    descriptionChildren={<>
                                    	<Box>Customer satisfaction on tickets solved by Remarkable AI
										</Box>
                                    </>}
                                />
                                <Button sx={styles.viewDetailsBtn}
                                        component={RouterLink}
                                        to={{ pathname: `/support-details`,
                                            search: `?startDate=${startDate}&endDate=${endDate}&pageFilter=csat`
                                        }}
                                        variant="text">
                                    View details
                                </Button>
                            </Grid>
                        )
					}
					<Grid item xs={4} md={1}>
						<DataTeaser
							stackStyle={styles.dataTeaser}
							label={'Pre-Sale Conversations'}
							entry={{ value: performanceMetrics.data ? performanceMetrics.data.preSaleQuestions : null, formatStyle: 'percent' }}
							descriptionChildren={<>
								<Box>Percent of support tickets about pricing, product availability, etc.</Box>
							</>}
						/>
						<Button sx={styles.viewDetailsBtn}
								component={RouterLink}
								to={{ pathname: `/support-details`,
									search: `?startDate=${startDate}&endDate=${endDate}&pageFilter=presale`
								}}
								variant="text">
							View details
						</Button>
					</Grid>
				</Grid>
			}

		</DashboardCard>
	)
}

const styles = {
	root: {
		py: { xs: 2, md: 4 },
	},
	viewDetailsBtn: {
		color: '#125e82',
		textTransform: 'capitalize',
		padding: '0px !important',
		height: '25%',
		margin:'0 auto',
		position: 'absolute',
		bottom: '20px',
		fontFamily: 'FustatSemiBold',
		fontSize: '14px',
		letterSpacing: '0em',
		':hover': {
			backgroundColor: 'unset',
			color: '#1c465e',
		}
	},
	dataTeaser: {
		marginBottom: '35px'
	},
	bodyLoading: {
        py: 4,
        display: 'flex',
        justifyContent: 'center',
    },
	bodyLoadingSpinner: {
		color: '#003300',
	},
	label: {
        m: 0,
        fontSize: '14px',
        fontFamily: 'LarsseitBold',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
	},
}